import {  useLocation } from "wouter";
import { Bell, HelpCircle, Plus, Menu, Clock, AlertTriangle, Users, Calendar, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { UploadPOForm } from "@/components/po/UploadPOForm";
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { clearSelectedPlatform, setPlatformOptions, setSelectedPlatform } from "../../store/slices/platformOptions";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { clearUser } from "@/store/slices/userSlice";
import axios from "axios";
import BASE_URL from "@/constants";

// Notification types
interface Notification {
  id: string;
  type: 'pending_alert' | 'weekly_digest' | 'escalation';
  title: string;
  message: string;
  timestamp: Date;
  severity: 'low' | 'medium' | 'high' | 'critical';
  poNumber?: string;
  teamName?: string;
  hoursRemaining?: number;
  isRead: boolean;
}

export const Header = () => {
   const companyName = useSelector((state: any) => state.user.company_name);
  const dispatch = useDispatch();
  const platformOptions = useSelector((state: RootState) => state.platformOptions.options);
  const selectedPlatform = useSelector((state: RootState) => state.platformOptions.selectedPlatform);

  const [ ,setLocation] = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);


const changePlatform = (value: string) => {
  dispatch(setSelectedPlatform(value));
  // localStorage.setItem("selectedPlatform", value); // optional
};

useEffect(() => {
  if (!companyName) return;

  fetch(`${BASE_URL}/onboarding/findbyattribute?attribute=companyName&value=${companyName}`)
    .then(res => res.json())
    .then(data => {
      const allowedPlatforms = (data.platforms || []).map((p: any) => ({
        value: p.platformName,
        label: p.platformName,
      }));

      dispatch(setPlatformOptions(allowedPlatforms));

      // If current selectedPlatform is not in allowed, reset to first allowed
      const isValid = allowedPlatforms.some(p => p.value === selectedPlatform);
      const first = allowedPlatforms[0]?.value || "";

      if (!isValid) {
        dispatch(setSelectedPlatform(first));
      }
    })
    .catch(() => {
      dispatch(setPlatformOptions([]));
      dispatch(clearSelectedPlatform());
    });
}, [selectedPlatform, companyName]);

  // No localStorage sync for selectedPlatform; Redux only

  // Define the expected shape of the purchase orders data
  interface PurchaseOrdersResponse {
    items: any[];
    [key: string]: any;
  }

  // Fetch PO data for notification analysis
  const { data: purchaseOrders } = useQuery<PurchaseOrdersResponse>({
    queryKey: ["/api/purchase-orders"],
    refetchInterval: 30000, // Refresh every 30 seconds
  });



  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'pending_alert': return <Clock className="h-4 w-4" />;
      case 'weekly_digest': return <Calendar className="h-4 w-4" />;
      case 'escalation': return <AlertTriangle className="h-4 w-4" />;
      default: return <Bell className="h-4 w-4" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 border-red-300 text-red-800';
      case 'high': return 'bg-orange-100 border-orange-300 text-orange-800';
      case 'medium': return 'bg-yellow-100 border-yellow-300 text-yellow-800';
      case 'low': return 'bg-blue-100 border-blue-300 text-blue-800';
      default: return 'bg-gray-100 border-gray-300 text-gray-800';
    }
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;

  // Logout handler
  const handleLogout = async() => {
    // localStorage.clear();
    // sessionStorage.clear();
    try {
     const res =  await axios.get(BASE_URL+"/users/logout");
   
  dispatch(clearUser())
    
    setLocation("/login");
    } catch (error) {
      
    }
    

    
  };

  return (
    <>
      {/* Top navbar */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200">
        <div className="flex justify-between h-16">
          <div className="flex px-4 md:px-0">
            <div className="flex items-center md:hidden">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-gray-500 hover:text-gray-900"
              >
                <Menu className="h-6 w-6" />
              </Button>
            </div>
            <div className="flex items-center md:ml-6">
              <div className="relative flex items-center">
                <div className=" py-2 border-gray-200 flex items-center">
                  <div className="font-heading font-semibold text-lg text-primary">PO Management System</div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center px-4">
            {/* Platform Dropdown */}
            <div className="mr-4">
              <Select value={selectedPlatform} onValueChange={changePlatform}>
                <SelectTrigger className="w-48 h-10 border border-purple-300 rounded-md bg-gradient-to-r from-purple-100 to-purple-200 hover:from-purple-150 hover:to-purple-250 transition-all duration-200 focus:ring-2 focus:ring-purple-400 focus:border-transparent shadow-sm">
                  <SelectValue placeholder="Select Platform">
                    <span className="text-purple-800 font-medium text-sm">{selectedPlatform}</span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="w-48 border border-purple-200 bg-white shadow-lg">
                  {platformOptions.map((platform) => (
                    <SelectItem
                      key={platform.value}
                      value={platform.value}
                      className="hover:bg-purple-50 focus:bg-purple-100 cursor-pointer px-3 py-2"
                    >
                      <div className="flex items-center">
                        <div className="w-2 h-2 bg-purple-500 rounded-full mr-3"></div>
                        <span className="text-gray-800 text-sm font-medium">{platform.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* <Popover open={isNotificationOpen} onOpenChange={setIsNotificationOpen}>
              <PopoverTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="text-gray-400 hover:text-gray-500 relative"
                >
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <Badge 
                      variant="destructive" 
                      className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
                    >
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-96 p-0" align="end">
                <Card className="border-0 shadow-lg">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg font-semibold">Notifications</CardTitle>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs">
                          {notifications.length} total
                        </Badge>
                        {unreadCount > 0 && (
                          <Badge variant="destructive" className="text-xs">
                            {unreadCount} unread
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <ScrollArea className="h-80">
                      {notifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-32 text-gray-500">
                          <CheckCircle className="h-8 w-8 mb-2 text-gray-400" />
                          <p className="text-sm font-medium">All caught up!</p>
                          <p className="text-xs">No new notifications</p>
                        </div>
                      ) : (
                        notifications.map((notification, index) => (
                          <div key={notification.id}>
                            <div 
                              className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                                !notification.isRead ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                              }`}
                              // onClick={() => markAsRead(notification.id)}
                            >
                              <div className="flex items-start gap-3">
                                <div className={`p-2 rounded-full ${getSeverityColor(notification.severity)}`}>
                                  {getNotificationIcon(notification.type)}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between mb-1">
                                    <h4 className="text-sm font-medium text-gray-900 truncate">
                                      {notification.title}
                                    </h4>
                                    <span className="text-xs text-gray-500 ml-2">
                                      {notification.timestamp.toLocaleTimeString()}
                                    </span>
                                  </div>
                                  <p className="text-sm text-gray-600 mb-2">
                                    {notification.message}
                                  </p>
                                  <div className="flex items-center gap-2">
                                    {notification.poNumber && (
                                      <Badge variant="outline" className="text-xs">
                                        {notification.poNumber}
                                      </Badge>
                                    )}
                                    {notification.teamName && (
                                      <Badge variant="secondary" className="text-xs">
                                        <Users className="h-3 w-3 mr-1" />
                                        {notification.teamName}
                                      </Badge>
                                    )}
                                    {notification.hoursRemaining && (
                                      <Badge 
                                        variant={notification.hoursRemaining <= 24 ? "destructive" : "default"} 
                                        className="text-xs"
                                      >
                                        <Clock className="h-3 w-3 mr-1" />
                                        {notification.hoursRemaining}h left
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                            {index < notifications.length - 1 && <Separator />}
                          </div>
                        ))
                      )}
                    </ScrollArea>
                    {notifications.length > 0 && (
                      <>
                        <Separator />
                        <div className="p-3 bg-gray-50">
                          <div className="grid grid-cols-3 gap-2 text-xs">
                            <div className="text-center">
                              <div className="flex items-center justify-center mb-1">
                                <Clock className="h-3 w-3 mr-1 text-orange-500" />
                                <span className="font-medium">Pending</span>
                              </div>
                              <span className="text-gray-600">
                                {notifications.filter(n => n.type === 'pending_alert').length}
                              </span>
                            </div>
                            <div className="text-center">
                              <div className="flex items-center justify-center mb-1">
                                <Calendar className="h-3 w-3 mr-1 text-blue-500" />
                                <span className="font-medium">Weekly</span>
                              </div>
                              <span className="text-gray-600">
                                {notifications.filter(n => n.type === 'weekly_digest').length}
                              </span>
                            </div>
                            <div className="text-center">
                              <div className="flex items-center justify-center mb-1">
                                <AlertTriangle className="h-3 w-3 mr-1 text-red-500" />
                                <span className="font-medium">Escalated</span>
                              </div>
                              <span className="text-gray-600">
                                {notifications.filter(n => n.type === 'escalation').length}
                              </span>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              </PopoverContent>
            </Popover> */}
            {/* <Button variant="ghost" size="icon" className="ml-4 text-gray-400 hover:text-gray-500">
              <HelpCircle className="h-5 w-5" />
            </Button> */}
            <div className="ml-4 relative">
              <Dialog>
                <DialogTrigger asChild>
                  <Button className="flex items-center text-sm">
                    <Plus className="mr-2 h-4 w-4" />
                    New PO
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[425px]">
                  <UploadPOForm />
                </DialogContent>
              </Dialog>
            </div>
            <Button
              variant="outline"
              size="lg"
              className="ml-4  bg-red-500 text-white hover:text-black hover:bg-red-300 border-red-200"
              onClick={handleLogout}
            >
              Logout
            </Button>
          </div>
        </div>
      </div>

     
      {/* Mobile Menu (hidden on desktop) */}
 
    </>
  );
};
